import { MutableRefObject, RefObject, useCallback, useEffect, useRef } from 'react';

import {
  applyScalingOnPoint,
  applyTranslation,
  getIdentityTransformationMatrix2D,
  getTransformationMatrix2DParams,
  TransformationMatrix2D,
} from 'dashboard/lib/utils/cssTransformationMatrix';

interface IPanZoomOnWheel {
  minimumScaling: number;
  maximumScaling: number;
  transformationMatrixRef: MutableRefObject<TransformationMatrix2D>;
  boundingRef: RefObject<Element>;
  onTransformationChangeStart?: () => void;
  onTransformationMatrixChange?: (transformationMatrix: TransformationMatrix2D) => void;
  setPercentageScale?: (value: number) => void;
  preventDefaultOnPan?: boolean;
  preventPanOnScroll?: boolean;
}

/**
 * Make an element zoomable by pinch/zoom gestures, and pannable.
 */
export default function usePanZoomOnWheel<Element extends HTMLElement = HTMLDivElement>({
  minimumScaling,
  maximumScaling,
  transformationMatrixRef,
  boundingRef,
  onTransformationChangeStart,
  onTransformationMatrixChange,
  setPercentageScale,
  preventDefaultOnPan,
  preventPanOnScroll,
}: IPanZoomOnWheel) {
  // Ref to be attached to the element we want to zoom
  const zoomableRef = useRef<Element>();
  const containerRef = useRef<Element>();

  // We need gesture events for Safari. (Safari does not support wheel events in the same way as other browsers)
  // Similarly, the only browser supporting gesture events is Safari.
  const gestureStartScale = useRef<number>(1);
  const gestureStartX = useRef<number>(0);
  const gestureStartY = useRef<number>(0);
  const gestureLatestTransformationMatrix = useRef<TransformationMatrix2D>(
    getIdentityTransformationMatrix2D(),
  );

  useEffect(() => {
    // Add and clean up listeners
    // The reason we still need the onWheel handler on document is because
    // React's onWheel event listener is passive (and not possible to make non-passive), which causes
    // issues for Chrome. More info: https://github.com/facebook/react/issues/6436#issuecomment-638750608
    const containerRefCurrent = containerRef.current;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    containerRef.current.addEventListener('wheel', handleOnWheel, { passive: true });
    document.addEventListener('wheel', handleDocumentOnWheel, { passive: false });

    document.addEventListener('gesturestart', handleOnGestureStart);
    document.addEventListener('gesturechange', handleOnGestureChange);
    document.addEventListener('gestureend', handleOnGestureEnd);
    return () => {
      document.removeEventListener('gesturestart', handleOnGestureStart);
      document.removeEventListener('gesturechange', handleOnGestureChange);
      document.removeEventListener('gestureend', handleOnGestureEnd);
      document.removeEventListener('wheel', handleDocumentOnWheel);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      containerRefCurrent.removeEventListener('wheel', handleOnWheel);
    };
  }, [minimumScaling, maximumScaling]);

  /**
   * Manually set the transformation matrix on the element
   * @param transformationMatrix transformation matrix to apply on the zoomable ref
   * @returns nothing
   */
  const setTransformationMatrix = useCallback(
    (transformationMatrix: TransformationMatrix2D) => {
      window.requestAnimationFrame(() => {
        const matrixParams = getTransformationMatrix2DParams(transformationMatrix);
        if (transformationMatrixRef.current) {
          transformationMatrixRef.current = transformationMatrix;
        }
        if (zoomableRef.current) {
          zoomableRef.current.style.transform = `matrix(${matrixParams.scaleX}, ${matrixParams.skewY}, ${matrixParams.skewX}, ${matrixParams.scaleY}, ${matrixParams.translateX}, ${matrixParams.translateY})`;
        }
        onTransformationMatrixChange?.(transformationMatrix);
      });
    },
    [onTransformationMatrixChange, transformationMatrixRef],
  );

  /**
   * Pans the element in the direction specified by translateX and translateY
   * @param translateX number of pixels to move in the horizontal direction
   * @param translateY number of pixels to move in the vertical direction
   * @returns a boolean value that indicates whether the caller (who is trying to get a node to scroll to view)
   *  should continue calling panScreen (we should stop if both x and y pannings are cut off by our out of bounds check)
   */
  const panScreen = useCallback(
    (translateX: number, translateY: number): boolean => {
      if (zoomableRef.current == null) {
        return true;
      }

      onTransformationChangeStart?.();
      let panIsCutOffX = translateX === 0;
      let panIsCutOffY = translateY === 0;

      // check for out of bounds (stop it from showing more than half of the screen as outside the bounding rectangle of the element)
      const zoomableRect = zoomableRef.current.getBoundingClientRect();
      const { x: boundingRefMidpointX, y: boundingRefMidpointY } = getBoundingRefMidpoint();

      if (zoomableRect.left + translateX > boundingRefMidpointX) {
        translateX = boundingRefMidpointX - zoomableRect.left;
        panIsCutOffX = true;
      } else if (zoomableRect.right + translateX < boundingRefMidpointX) {
        translateX = boundingRefMidpointX - zoomableRect.right;
        panIsCutOffX = true;
      }

      if (zoomableRect.top + translateY > boundingRefMidpointY) {
        translateY = boundingRefMidpointY - zoomableRect.top;
        panIsCutOffY = true;
      } else if (zoomableRect.bottom + translateY < boundingRefMidpointY) {
        translateY = boundingRefMidpointY - zoomableRect.bottom;
        panIsCutOffY = true;
      }

      const currentScale = getTransformationMatrix2DParams(transformationMatrixRef.current).scaleX;

      // do the translation calculations
      const newTransformationMatrix = applyTranslation(
        transformationMatrixRef.current,
        translateX / currentScale,
        translateY / currentScale,
      );

      // update matrix and style
      setTransformationMatrix(newTransformationMatrix);
      return !(panIsCutOffX && panIsCutOffY);
    },
    [onTransformationChangeStart, setTransformationMatrix, transformationMatrixRef],
  );

  /**
   * Scales the element on a point
   * @param scaling the factor to scale by
   * @param originX the x position of the point to scale on
   * @param originY the y position of the point to scale on
   * @returns the new transformation matrix, or nothing if we don't end up scaling
   */
  const scaleScreen = useCallback(
    (scaling: number, originX: number, originY: number) => {
      if (zoomableRef.current == null) {
        return;
      }

      onTransformationChangeStart?.();
      const newTransformationMatrix = applyScalingOnPoint(
        transformationMatrixRef.current,
        scaling,
        originX,
        originY,
      );
      const matrixParams = getTransformationMatrix2DParams(newTransformationMatrix);
      const newScale = Math.round(matrixParams.scaleX * 100);

      // prevent over/under scaling
      // we want to take the lowest value for minimum scale, and highest for maximum scale
      // so we round down for minimum and round up for maximum
      if (
        newScale < Math.floor(minimumScaling * 100) ||
        newScale > Math.ceil(maximumScaling * 100)
      ) {
        return;
      }

      // update matrix and style
      setTransformationMatrix(newTransformationMatrix);

      return newTransformationMatrix;
    },
    [
      maximumScaling,
      minimumScaling,
      onTransformationChangeStart,
      setTransformationMatrix,
      transformationMatrixRef,
    ],
  );

  const handleDocumentOnWheel = useCallback((e: WheelEvent) => {
    if (
      e.ctrlKey ||
      e.metaKey ||
      (preventDefaultOnPan && containerRef.current?.contains(e.target as Node))
    ) {
      e.preventDefault();
    }
    e.stopPropagation();
  }, []);

  const handleOnWheel = useCallback(
    (e: WheelEvent) => {
      if (zoomableRef.current == null) {
        return;
      }
      if (boundingRef?.current) {
        const boundingRect = boundingRef.current.getBoundingClientRect();
        // if the action is out of bounds, ignore
        if (
          e.clientX < boundingRect.left ||
          e.clientX > boundingRect.right ||
          e.clientY < boundingRect.top ||
          e.clientY > boundingRect.bottom
        ) {
          return;
        }
      }

      // ctrlKey is true for zooming on trackpad
      // also support e.metaKey and e.altKey for alt/option.
      if (e.ctrlKey || e.metaKey || e.altKey) {
        const zoomableRect = zoomableRef.current.getBoundingClientRect();
        // get the scaling parameters
        // upperbound to 1.3, and lowerbound to 0.7 since different browsers can have large deltas
        // e.g. Windows has 100 and -100 as the deltaY
        const scaling = Math.min(1.3, Math.max(0.7, 1 - e.deltaY * 0.01));
        const originX = e.clientX - zoomableRect.left;
        const originY = e.clientY - zoomableRect.top;
        const newTransformationMatrix = scaleScreen(scaling, originX, originY);
        if (setPercentageScale && newTransformationMatrix) {
          const matrixParams = getTransformationMatrix2DParams(newTransformationMatrix);
          // round scale to the nearest whole number
          // round down if fraction is < .5, and up if > .5
          setPercentageScale(Math.round(matrixParams.scaleX * 100));
        }
      } else {
        // get the panning parameters
        const xDif = -e.deltaX * 2;
        const yDif = -e.deltaY * 2;
        if (!preventPanOnScroll) {
          panScreen(xDif, yDif);
        }
        if (preventDefaultOnPan === true) {
          return false;
        }
      }
    },
    [
      boundingRef,
      panScreen,
      preventDefaultOnPan,
      preventPanOnScroll,
      scaleScreen,
      setPercentageScale,
      transformationMatrixRef,
    ],
  );

  // e is a GestureEvent. https://developer.apple.com/documentation/webkitjs/gestureevent#declaration
  const handleOnGestureStart = useCallback(
    (e: any) => {
      e.preventDefault();
      if (zoomableRef.current == null) {
        return;
      }
      // set gesture start variables
      const zoomableRect = zoomableRef.current.getBoundingClientRect();
      gestureStartScale.current = getTransformationMatrix2DParams(
        transformationMatrixRef.current,
      ).scaleX;
      gestureStartX.current = e.clientX - zoomableRect.left;
      gestureStartY.current = e.clientY - zoomableRect.top;
    },
    [transformationMatrixRef],
  );

  const handleOnGestureChange = useCallback(
    (e: any) => {
      e.preventDefault();
      if (zoomableRef.current == null) {
        return;
      }
      if (boundingRef?.current) {
        const boundingRect = boundingRef.current.getBoundingClientRect();
        // if the action is out of bounds, ignore
        if (
          gestureStartX.current < boundingRect.left ||
          gestureStartX.current > boundingRect.right ||
          gestureStartY.current < boundingRect.top ||
          gestureStartY.current > boundingRect.bottom
        ) {
          return;
        }
      }

      // calculate scaling changes
      const endScaling = gestureStartScale.current * e.scale;
      if (endScaling < minimumScaling || endScaling > maximumScaling) {
        return;
      }
      const transformationMatrix = applyScalingOnPoint(
        transformationMatrixRef.current,
        e.scale,
        gestureStartX.current,
        gestureStartY.current,
      );
      gestureLatestTransformationMatrix.current = transformationMatrix;
      setTransformationMatrix(transformationMatrix);
    },
    [boundingRef, maximumScaling, minimumScaling, setTransformationMatrix, transformationMatrixRef],
  );

  const handleOnGestureEnd = useCallback(
    (e: any) => {
      e.preventDefault();
      // update matrix
      transformationMatrixRef.current = gestureLatestTransformationMatrix.current;
    },
    [transformationMatrixRef],
  );

  const getBoundingRefMidpoint = () => {
    if (boundingRef.current) {
      const boundingRefRect = boundingRef.current.getBoundingClientRect();
      return {
        x: boundingRefRect.left + boundingRefRect.width / 2,
        y: boundingRefRect.top + boundingRefRect.height / 2,
      };
    } else {
      return {
        x: window.innerWidth / 2,
        y: window.innerHeight / 2,
      };
    }
  };

  return { zoomableRef, containerRef, scaleScreen, panScreen, setTransformationMatrix };
}
